export const transformOrderDetailsResponse = (apiResponse) => {
  // Helper function to format date
  const formatDate = (dateArray) => {
    if (!dateArray) return null;
    const date = new Date(dateArray[0], dateArray[1] - 1, dateArray[2]);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Helper function to format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return null;
    const date = new Date(timestamp);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Helper function to format currency
  const formatCurrency = (amount, currency = "INR") => {
    if (amount === null || amount === undefined) return null;
    return `${currency} ${amount}`;
  };

  // Helper function to get tag color based on status
  const getStatusTagInfo = (status) => {
    switch (status?.toUpperCase()) {
      case "PAID":
        return { text: "Paid", color: "success" };
      case "DEFERRED":
        return { text: "Deferred", color: "warning" };
      case "PENDING":
        return { text: "Pending", color: "processing" };
      case "CANCELLED":
        return { text: "Cancelled", color: "error" };
      default:
        return { text: status, color: "default" };
    }
  };

  const {
    userInfoOrderSummary: summary = {},
    userInfoProgramDetailList: programList = [],
    userInfoPricingDetails: pricing = {},
    userInfoOrderDetails: order = {},
    // OOD-895. Absent on every order placed before add-ons existed, and on every
    // order that simply has none - the section then does not render at all.
    userInfoAddOnList: addOnList = [],
  } = apiResponse;

  const { currency } = summary;
  const statusTag = getStatusTagInfo(order?.orderStatus);

  return {
    summary: [
      {
        orderId: summary?.orderId,
        paymentSource: summary?.paymentSource,
        price: formatCurrency(summary?.price, currency),
        revenue: formatCurrency(summary?.revenue, currency),
        createdBy: summary?.createdBy,
        createdOn: formatTimestamp(summary?.createdOn),
        salesAgent: summary?.saleAgent,
        sourceUserId: summary?.userId,
        currency: summary?.currency,
      },
    ],
    program:
      programList?.map((program) => ({
        programName: program.programName,
        scheduleId: program.scheduleId?.toString(),
        offeringKey: program.offeringKey,
        scheduleStartDate: formatDate(program.scheduleStartDate),
        softLaunchDate: formatDate(program.softLaunchDate),
        learningType: program.learningType,
        workshopLocation: program.workshopLocation,
        programKey: program.programPackageKey,
        // Bundle Program component fields (OOD-895) - only set on bundle
        // orders, where the BE sends one row per component
        revenueSplit: program.revenueSplit,
        componentDiscount: program.componentDiscount,
        componentTaxPercent: program.componentTaxPercent,
        componentTax: program.componentTax,
        // The per-component ledger, sent as display strings once fully paid:
        // actual price - discount = taxable; + tax = cash collected; - tax = final revenue
        componentSplitPercent: program.componentSplitPercent,
        componentActualPrice: program.componentActualPrice,
        componentTaxableAmount: program.componentTaxableAmount,
        componentCashCollected: program.componentCashCollected,
        componentFinalRevenue: program.componentFinalRevenue,
        bundleComponentStatus: program.bundleComponentStatus,
        prismState: program.prismState,
        bundleOrderEntityId: program.bundleOrderEntityId,
      })) || [],
    pricing: {
      priceWithoutTax: formatCurrency(pricing?.priceWithoutTax, currency),
      tax: pricing?.tax ? `${pricing.tax}%` : null,
      taxValue: pricing?.taxValue,
      priceWithTax: formatCurrency(pricing?.priceWithTax, currency),
      taxAmount: formatCurrency(
        (pricing?.priceWithTax * pricing?.tax) / 100,
        currency
      ),
      taxDisplay: pricing?.tax
        ? `${formatCurrency(pricing?.taxValue, currency)} (${pricing.tax}%)`
        : null,
      valueAddedService: pricing?.valueAddedService,
      coupon: pricing?.coupon?.percentage
        ? `${pricing.coupon.percentage}%`
        : null,
      discount: formatCurrency(pricing?.discount, currency),
      grandPrice: formatCurrency(pricing?.grandTotal, currency),
    },
    order: {
      modeOfPayment: order?.modeOfPayment,
      lastPaymentDate: formatDate(order?.lastPaymentDate),
      paidAmount: formatCurrency(order?.paidAmount, currency),
      orderIdBillingRequestId: order?.billingRequestId,
      orderStatus: order?.orderStatus,
      orderBillingDate: formatDate(order?.billingDate),
      applicationCompletedOn: order?.applicationCompletionDate,
      applicationId: order?.applicationId || order?.applicantDataId?.toString(),
      applicationBatch: order?.applicationBatch,
    },
    /**
     * OOD-895. The add-on lines of this order, each its own SKU sold beside the
     * programme. Empty for an order with none, which is what keeps the section
     * off the page for every order placed before add-ons existed.
     *
     * Every figure comes from the order line as it was charged, so a currency is
     * taken per line rather than from the order summary - an add-on carries its
     * own, and reusing the summary's would silently relabel it.
     */
    addOns: (addOnList || []).map((addOn) => ({
      addOnCode: addOn.addOnCode,
      name: addOn.name,
      parentOfferingKey: addOn.parentOfferingKey,
      priceWithoutTax: formatCurrency(
        addOn.priceWithoutTax,
        addOn.currency || currency
      ),
      taxAmount: formatCurrency(addOn.taxAmount, addOn.currency || currency),
      discount: formatCurrency(addOn.discount, addOn.currency || currency),
      payableAmount: formatCurrency(
        addOn.payableAmount,
        addOn.currency || currency
      ),
      // Null and zero differ: null is an order written before weights were
      // stamped, zero is a line deliberately excluded from coupons.
      discountSplitWeight:
        addOn.discountSplitWeight === null ||
        addOn.discountSplitWeight === undefined
          ? null
          : String(addOn.discountSplitWeight),
    })),
    transferHistory: apiResponse?.transferHistory,
    tag: statusTag,
    // Add-on lines (OOD-895) - their own list on the BE, kept numeric here;
    // each add-on line is flagged paid / unpaid from its payment entry
    addOns: [
      ...transformAddOnLines(
        apiResponse?.userInfoAddOnList,
        apiResponse?.userInfoAddOnPaymentList
      ),
      ...transformDeferralFeeLines(apiResponse?.userInfoDeferralFeeList),
    ],
    // OOD-895 D4 - the Sales Link that collects this order, when it has a
    // live one; the deferral fee link on the order a paid deferral moved the
    // learner to
    paymentLink: transformPaymentLink(apiResponse?.userInfoPaymentLink),
  };
};

/**
 * OOD-895 D4 - admin-utility's `userInfoPaymentLink` (served only when the
 * order has a live Sales Link), as the order page shows it. Null when the
 * BE sent none, so an order without a link renders exactly as before.
 */
export const transformPaymentLink = (link) => {
  if (!link || typeof link !== "object" || !link.salesLinkId) return null;
  const deferralFeeLink = link.deferralFeeLink === true;
  return {
    salesLinkId: link.salesLinkId,
    url: link.url || null,
    linkStatus: link.linkStatus || null,
    purpose: link.purpose || null,
    deferralFeeLink,
    partialPaymentAllowed: link.partialPaymentAllowed === true,
    referenceBillingRequestId: link.referenceBillingRequestId || null,
    label: deferralFeeLink ? "Deferral fee payment link" : "Payment link",
  };
};

/**
 * A number out of whatever the BE sent for an amount: a number as it is, a
 * display string like "INR 90000" or "INR 1,23,456.50" by its digits, and
 * anything empty or non-numeric as null so a total built on it stays null
 * rather than silently dropping the line.
 */
export const parseAmount = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const match = String(value)
    .replace(/,/g, "")
    .match(/-?\d+(\.\d+)?/);
  return match ? Number(match[0]) : null;
};

/**
 * The kinds of line admin-utility lists beside the programme: an add-on sold
 * with it, or the deferral fee a paid deferral put on the order. A line that
 * does not say (an older BE) is an add-on, which is all that list ever held.
 */
export const ORDER_LINE_TYPES = {
  ADD_ON: "ADD_ON",
  DEFERRAL_FEE: "DEFERRAL_FEE",
};

const lineTypeOf = (row) =>
  row?.lineType === ORDER_LINE_TYPES.DEFERRAL_FEE
    ? ORDER_LINE_TYPES.DEFERRAL_FEE
    : ORDER_LINE_TYPES.ADD_ON;

/**
 * OOD-895 - the add-on and deferral-fee lines of an order, from
 * admin-utility's `userInfoAddOnList` (served only when the order has any).
 * Every figure is what the ORDER LINE recorded at purchase; the numbers stay
 * numbers so the Revenue Split table can add them up.
 */
/**
 * The deferral-fee line a paid deferral put on the order, from admin-utility's
 * `userInfoDeferralFeeList` (served only when the order carries one). Kept on
 * its own list on the BE, apart from the add-ons another team owns; here it
 * joins the add-on lines so the Revenue Summary table lays them out together.
 */
export const transformDeferralFeeLines = (rows) =>
  (rows || []).map((row, index) => ({
    key: `deferral-fee-${index}`,
    lineType: ORDER_LINE_TYPES.DEFERRAL_FEE,
    addOnCode: null,
    name: row.name || "Deferral Fee",
    parentOfferingKey: null,
    priceWithoutTax: parseAmount(row.priceWithoutTax),
    taxAmount: parseAmount(row.taxAmount),
    discount: parseAmount(row.discount),
    payableAmount: parseAmount(row.payableAmount),
    discountSplitWeight: null,
    currency: row.currency ?? null,
  }));

/**
 * OOD-895 - a lookup from an add-on line to its entry in admin-utility's
 * `userInfoAddOnPaymentList` (one entry per `userInfoAddOnList` row, same
 * filter and order): the n-th line with code X takes the n-th entry with
 * code X, since a repeatable add-on can sit on the order more than once.
 * `paidFor(code)` is true / false from the entry, or undefined when there is
 * no list (an older BE), no entry left for the code, or no boolean on it.
 */
const addOnPaymentMatcher = (payments) => {
  if (!Array.isArray(payments)) return () => undefined;
  const byCode = new Map();
  payments.forEach((entry) => {
    const code = entry?.addOnCode ?? null;
    if (!byCode.has(code)) byCode.set(code, []);
    byCode.get(code).push(entry);
  });
  const taken = new Map();
  return (addOnCode) => {
    const code = addOnCode ?? null;
    const occurrence = taken.get(code) || 0;
    taken.set(code, occurrence + 1);
    const entry = (byCode.get(code) || [])[occurrence];
    return typeof entry?.paid === "boolean" ? entry.paid : undefined;
  };
};

/**
 * `payments` is `userInfoAddOnPaymentList`; each line gets `paid` from it
 * (see `addOnPaymentMatcher`) - the Revenue Summary leaves unpaid add-ons out.
 */
export const transformAddOnLines = (rows, payments) => {
  const paidFor = addOnPaymentMatcher(payments);
  return (rows || []).map((row, index) => ({
    key: `${
      lineTypeOf(row) === ORDER_LINE_TYPES.DEFERRAL_FEE
        ? "deferral-fee"
        : "add-on"
    }-${row.addOnCode || "line"}-${index}`,
    lineType: lineTypeOf(row),
    addOnCode: row.addOnCode ?? null,
    name:
      row.name ||
      row.addOnCode ||
      (lineTypeOf(row) === ORDER_LINE_TYPES.DEFERRAL_FEE
        ? "Deferral Fee"
        : null),
    parentOfferingKey: row.parentOfferingKey ?? null,
    priceWithoutTax: parseAmount(row.priceWithoutTax),
    taxAmount: parseAmount(row.taxAmount),
    discount: parseAmount(row.discount),
    payableAmount: parseAmount(row.payableAmount),
    discountSplitWeight: parseAmount(row.discountSplitWeight),
    currency: row.currency ?? null,
    paid: paidFor(row.addOnCode),
  }));
};

export const REVENUE_SPLIT_ROW_KINDS = {
  HEADING: "HEADING",
  COMPONENT: "COMPONENT",
  SUBTOTAL: "SUBTOTAL",
  ADD_ON: "ADD_ON",
  DEFERRAL_FEE: "DEFERRAL_FEE",
  TOTAL: "TOTAL",
};

export const isRevenueSplitHeadingRow = (row) =>
  row?.kind === REVENUE_SPLIT_ROW_KINDS.HEADING;

export const isRevenueSplitTotalRow = (row) =>
  row?.kind === REVENUE_SPLIT_ROW_KINDS.SUBTOTAL ||
  row?.kind === REVENUE_SPLIT_ROW_KINDS.TOTAL;

const isBundleComponentRow = (row) =>
  Boolean(row && (row.revenueSplit || row.bundleComponentStatus));

/** A sum, or null as soon as one of its parts is unknown - never a partial figure. */
const sumOrNull = (values) =>
  values.length && values.every((value) => value !== null)
    ? Math.round(values.reduce((total, value) => total + value, 0) * 100) / 100
    : null;

const difference = (a, b) =>
  a === null || b === null ? null : Math.round((a - b) * 100) / 100;

/** This row's discount as a share of the order's, to one decimal; null when either is unknown. */
const shareOf = (part, whole) =>
  part === null || whole === null || whole === 0
    ? null
    : Math.round((part / whole) * 1000) / 10;

/**
 * OOD-895 - the Revenue Split table of a bundle order.
 *
 * Reads as a ledger, ex-tax figures plus each line's tax and cash collected: the bundle's components (their share
 * of the bundle is in the label; the order-level discount reached them
 * through the bundle line, so their own Discount % is left blank), the bundle
 * subtotal, then every add-on sold with the bundle, then the deferral fee a
 * paid deferral put on the order, then the grand total. "Discount %" is a
 * row's discount as a share of the whole order's - the discount-split weights
 * as they actually landed, computed from the money rather than re-derived
 * from weights.
 *
 * The component ledger arrives as display strings and only once fully paid;
 * before that its money is unknown and so is every total built on it. The
 * subtotal row is left out when there is no other line: it would only repeat
 * the grand total.
 *
 * An add-on line flagged `paid: false` (from `userInfoAddOnPaymentList`) is
 * left out until it is paid, with the grand total and every Discount %
 * computed without it; the Add-ons heading goes when none is left.
 */
export const revenueSplitRows = (program, addOns) => {
  const components = (program || [])
    .filter(isBundleComponentRow)
    .map((row, index) => {
      const revenueBeforeDiscount = parseAmount(row.componentActualPrice);
      const discountAllocated = parseAmount(row.componentDiscount);
      const netRevenue =
        parseAmount(row.componentFinalRevenue) ??
        difference(revenueBeforeDiscount, discountAllocated);
      const tax = parseAmount(row.componentTax);
      const cashCollected =
        parseAmount(row.componentCashCollected) ?? sumOrNull([netRevenue, tax]);
      const share = row.componentSplitPercent
        ? ` - ${row.componentSplitPercent} share`
        : "";
      return {
        key: `component-${row.offeringKey || row.programName}-${index}`,
        kind: REVENUE_SPLIT_ROW_KINDS.COMPONENT,
        item: `${row.programName || row.offeringKey || "Component"}${share}`,
        revenueBeforeDiscount,
        discountAllocated,
        netRevenue,
        tax,
        cashCollected,
      };
    });
  const lineRow = (line, index) => {
    const deferralFee = lineTypeOf(line) === ORDER_LINE_TYPES.DEFERRAL_FEE;
    return {
      key:
        line.key ||
        `${deferralFee ? "deferral-fee" : "add-on"}-${
          line.addOnCode || "line"
        }-${index}`,
      kind: deferralFee
        ? REVENUE_SPLIT_ROW_KINDS.DEFERRAL_FEE
        : REVENUE_SPLIT_ROW_KINDS.ADD_ON,
      // the deferral SKU is minted under the bundle's own product, so its
      // product name is the bundle's; the row says what it is instead
      item: deferralFee
        ? "Deferral Fee"
        : line.name || line.addOnCode || "Add-on",
      discountSplitWeight: line.discountSplitWeight ?? null,
      revenueBeforeDiscount: line.priceWithoutTax ?? null,
      discountAllocated: line.discount ?? null,
      netRevenue: difference(
        line.priceWithoutTax ?? null,
        line.discount ?? null
      ),
      tax: line.taxAmount ?? null,
      cashCollected:
        line.payableAmount ??
        sumOrNull([
          difference(line.priceWithoutTax ?? null, line.discount ?? null),
          line.taxAmount ?? null,
        ]),
    };
  };
  // an add-on whose order line is not paid yet is not revenue: it is left out
  // of the rows and of every total and share (unknown `paid` still shows);
  // a deferral-fee line is always shown
  const isUnpaidAddOn = (line) =>
    lineTypeOf(line) !== ORDER_LINE_TYPES.DEFERRAL_FEE && line?.paid === false;
  const otherLines = (addOns || [])
    .map((line, index) => (isUnpaidAddOn(line) ? null : lineRow(line, index)))
    .filter(Boolean);
  const addOnRows = otherLines.filter(
    (row) => row.kind === REVENUE_SPLIT_ROW_KINDS.ADD_ON
  );
  const deferralRows = otherLines.filter(
    (row) => row.kind === REVENUE_SPLIT_ROW_KINDS.DEFERRAL_FEE
  );
  if (!components.length && !addOnRows.length && !deferralRows.length) {
    return [];
  }

  const heading = (key, item) => ({
    key: `heading-${key}`,
    kind: REVENUE_SPLIT_ROW_KINDS.HEADING,
    item,
    revenueBeforeDiscount: null,
    discountAllocated: null,
    netRevenue: null,
    tax: null,
    cashCollected: null,
  });
  const subtotal = {
    key: "component-subtotal",
    kind: REVENUE_SPLIT_ROW_KINDS.SUBTOTAL,
    item: "Component Subtotal",
    revenueBeforeDiscount: sumOrNull(
      components.map((row) => row.revenueBeforeDiscount)
    ),
    discountAllocated: sumOrNull(
      components.map((row) => row.discountAllocated)
    ),
    netRevenue: sumOrNull(components.map((row) => row.netRevenue)),
    tax: sumOrNull(components.map((row) => row.tax)),
    cashCollected: sumOrNull(components.map((row) => row.cashCollected)),
  };
  const moneyRows = [...components, ...addOnRows, ...deferralRows];
  const total = {
    key: "grand-total",
    kind: REVENUE_SPLIT_ROW_KINDS.TOTAL,
    item: "Grand Total",
    revenueBeforeDiscount: sumOrNull(
      moneyRows.map((row) => row.revenueBeforeDiscount)
    ),
    discountAllocated: sumOrNull(moneyRows.map((row) => row.discountAllocated)),
    netRevenue: sumOrNull(moneyRows.map((row) => row.netRevenue)),
    tax: sumOrNull(moneyRows.map((row) => row.tax)),
    cashCollected: sumOrNull(moneyRows.map((row) => row.cashCollected)),
  };

  const withShare = (row) => ({
    ...row,
    discountPercent: shareOf(row.discountAllocated, total.discountAllocated),
  });
  const noShare = (row) => ({ ...row, discountPercent: null });

  // Components, then Add-ons, then Deferral: each group only when it has rows,
  // each under its own heading, the components closed by their subtotal.
  return [
    ...(components.length
      ? [
          noShare(heading("components", "Components")),
          ...components.map(noShare),
          withShare(subtotal),
        ]
      : []),
    ...(addOnRows.length
      ? [noShare(heading("add-ons", "Add-ons")), ...addOnRows.map(withShare)]
      : []),
    ...(deferralRows.length
      ? [
          noShare(heading("deferral", "Deferral")),
          ...deferralRows.map(withShare),
        ]
      : []),
    withShare(total),
  ];
};

/** "INR 90000" / "INR 1234.5", or null so the cell shows N/A. */
export const formatRevenueAmount = (value, currency) => {
  if (value === null || value === undefined) return null;
  const amount = Math.round(value * 100) / 100;
  return currency ? `${currency} ${amount}` : String(amount);
};

/** "80%" / "88.9%", or null so the cell shows N/A. */
export const formatRevenuePercent = (value) =>
  value === null || value === undefined ? null : `${value}%`;

/**
 * OOD-895 - "dd/mm/yyyy" for a component move timestamp, whichever way
 * order-service sends it (epoch millis or ISO); anything unparseable is shown
 * as it arrived.
 */
const formatMovedAt = (movedAt) => {
  if (!movedAt) return null;
  const date = new Date(movedAt);
  return Number.isNaN(date.getTime())
    ? movedAt
    : date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
};

/**
 * OOD-895 - rows of order-service
 * `GET /order/bundle/{groupOrderId}/component-transfers` for the bundle Move
 * History table (served newest first, and rendered in that order). The
 * categorised reason and the agent's free text are shown as one line.
 */
export const transformBundleComponentTransfers = (rows) =>
  (rows || []).map((row, index) => ({
    key: row.id || `${row.componentKey}-${index}`,
    sequence: row.sequence ?? null,
    componentKey: row.componentKey,
    from: row.fromScheduleId ?? null,
    to: row.toScheduleId ?? null,
    reason: [row.reason, row.reasonText].filter(Boolean).join(" - ") || null,
    source: row.source ?? null,
    actor: row.actor ?? null,
    movedAt: formatMovedAt(row.movedAt),
  }));

/**
 * Prism enrolment retry (OOD-895). The row button retries ONE component; the
 * column header offers every component in one go. Both read
 * bundleComponentStatus, the field the BE derives from prism_state.
 */
export const PRISM_RETRY_ALL = "all";

/** True when any component row of a bundle order is Failed in Prism. */
export const hasFailedBundleComponent = (programRows) =>
  (programRows || []).some((row) => row?.bundleComponentStatus === "Failed");

/** The spinner key for one retry: the order line plus the component it targets. */
export const prismRetryKey = (orderEntityId, componentOfferingKey) =>
  `${orderEntityId}|${componentOfferingKey || PRISM_RETRY_ALL}`;

/**
 * What to tell the agent once a retry answers. `failed` covers both a Prism
 * error and a component the BE could not reach (no workshop code, no
 * curriculumId), which the audit table explains per row.
 */
export const prismRetryMessage = (data, componentOfferingKey) => {
  const attempted = data?.attempted ?? 0;
  const succeeded = data?.succeeded ?? 0;
  const failed = data?.failed ?? 0;
  const target = componentOfferingKey
    ? `Component ${componentOfferingKey}`
    : "Prism enrolment";
  if (attempted === 0) {
    return { level: "warning", text: `${target}: nothing to retry` };
  }
  if (failed > 0) {
    return {
      level: "warning",
      text: `${target} retried: ${succeeded} succeeded, ${failed} still failing`,
    };
  }
  return {
    level: "success",
    text: `${target} retried: ${succeeded} of ${attempted} succeeded`,
  };
};
